import { useMutation } from '@apollo/client/react';
import {
  type BasicCustomerFragment,
  type CustomerPreferences,
  EarStyle,
  HairLength,
  Neckline,
  Parting,
  UpdatePreferencesDocument,
} from '@drycut/graphql';
import { Navigation } from '@drycut/navigation';
import { ActionButtons, Button, CheckBox, Heading, Link, Para, Stack } from '@drycut/ui-components';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';

import { CommonOptions } from './CommonOptions';
import ControlledHairLengthToggle from './ControlledHairLengthToggle';
import LongHairOptions from './LongHairOptions';
import ShortHairOptions from './ShortHairOptions';

type Props = {
  customer: BasicCustomerFragment;
};

export function Preferences({ customer }: Props): React.ReactElement {
  const { preferences } = customer;
  const { value: privacyPolicyAccepted, toggle: togglePrivacyPolicy } = useBoolean(false);
  const { value: submitted, setTrue: setSubmitted } = useBoolean(false);
  const [updatePreferences, { loading }] = useMutation(UpdatePreferencesDocument);

  const formMethods = useForm<CustomerPreferences>({
    defaultValues: {
      submittedPreferences: preferences?.submittedPreferences || true,
      hairLength: preferences?.hairLength || HairLength.NotDefined,
      lengthSides: preferences?.lengthSides || 0, // We use ?? to allow the value 0.
      lengthTop: preferences?.lengthTop || 0,
      ears: preferences?.ears || EarStyle.NotDefined,
      neckline: preferences?.neckline || Neckline.NotDefined,
      layers: preferences?.layers || undefined,
      backStyle: preferences?.backStyle || undefined,
      parting: preferences?.parting || Parting.NotDefined,
      cutEyebrows: preferences?.cutEyebrows || undefined,
      notes: preferences?.notes || '',
    },
  });

  const hairLength = useWatch({ control: formMethods.control, name: 'hairLength' });

  const onSubmit = async (data: CustomerPreferences) => {
    const updatePreferencesResult = await updatePreferences({
      variables: {
        input: {
          code: customer.code,
          data: {
            ...data,
            submittedPreferences: true,
          },
        },
      },
    });

    if (updatePreferencesResult.data?.updatePreferences?.customer) {
      setSubmitted();
    }
  };

  if (submitted) {
    return (
      <Stack gap={10} className="items-center font-aribau text-center text-pretty">
        <Heading variant="h2">Cut gespeichert</Heading>

        <Para>
          Super! Beim nächsten Mal wird es einfacher für unsere Stylisten Dir wieder Deinen
          Lieblingscut cu schneiden. Wir freuen uns auf Dich!
        </Para>
        <Button as="a" href={Navigation.HOME_URL} fill="outlined" className="lg:w-64">
          Schliessen
        </Button>
      </Stack>
    );
  }

  return (
    <Stack as="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
      <Stack gap={6} className="items-center font-aribau text-center text-pretty">
        <Heading variant="h2">Meine Präferenzen</Heading>

        <Heading variant="h4">{customer.name}</Heading>

        <Para className="text-center">
          Hilf uns beim nächsten Cut noch besser auf deine Wünsche einzugehen, indem du uns gezielte
          Angaben zu deinen persönlichen Präferenzen für deinen nächsten Cut gibst.
        </Para>

        <FormProvider {...formMethods}>
          <ControlledHairLengthToggle name="hairLength" />

          <Stack gap={2} className="w-full">
            {hairLength === HairLength.Short && <ShortHairOptions />}
            {hairLength === HairLength.Long && <LongHairOptions />}
            {hairLength === HairLength.NotDefined && (
              <Para className="mb-5">Bitte wählen Sie, ob Sie kurze oder lange Haare haben.</Para>
            )}

            <CommonOptions />
          </Stack>

          <CheckBox
            name="privacyPolicy"
            position="top"
            checked={privacyPolicyAccepted}
            onChange={togglePrivacyPolicy}
            label={
              <Para size="sm">
                Ich bin mit den{' '}
                <Link href={Navigation.TERMS_AND_CONDITIONS} target="_blank">
                  Datenschutzbestimmungen
                </Link>{' '}
                einverstanden.
              </Para>
            }
          />

          <ActionButtons>
            <Button type="submit" disabled={!privacyPolicyAccepted} loading={loading}>
              Speichern
            </Button>

            <Button as="a" href="/" fill="outlined">
              Schliessen
            </Button>
          </ActionButtons>
        </FormProvider>
      </Stack>
    </Stack>
  );
}
